import { getManagedCredential } from "../auth-refresh-coordinator"
import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/** Metadata only. Bytes and extracted text are resolved by the backend after access checks. */
export type HostedAgentSource = {
  id: string
  version: number
  title: string
  filename: string
  content_type: string
  size_bytes: number
  usage_context: string
  status: "ready" | "processing" | "failed"
  text_chars: number
  created_at: string
  updated_at: string
  scope: "agent" | "session"
}

export type HostedAgentSourceCapabilities = {
  accepted_extensions: string[]
  max_file_bytes: number
  max_sources: number
  images: boolean
  office: boolean
}

export type HostedAgentSourceList = {
  sources: HostedAgentSource[]
  capabilities: HostedAgentSourceCapabilities
}

export type HostedAgentSourceContent = {
  source: HostedAgentSource
  content: string
  offset: number
  next_offset: number | null
}

function sourcesUrl(agentRef: string, sessionId?: string | null): string {
  const base = `${BACKEND_URI}/agents/${encodeURIComponent(agentRef)}`
  return sessionId ? `${base}/sessions/${encodeURIComponent(sessionId)}/sources` : `${base}/sources`
}

export async function listHostedAgentSourcesApi(
  agentRef: string,
  sessionId?: string | null
): Promise<HostedAgentSourceList> {
  const response = await fetchWithAuthRetry(sourcesUrl(agentRef, sessionId), {
    headers: getHeaders(getManagedCredential()),
  })
  return (await response.json()) as HostedAgentSourceList
}

/** Task-only upload. The permanent knowledge library is explicitly admin-managed. */
export async function uploadHostedAgentSourceApi(
  agentRef: string,
  sessionId: string,
  upload: { file: File; requestId: string }
): Promise<HostedAgentSource> {
  const body = new FormData()
  body.append("file", upload.file)
  const headers = new Headers(getHeaders(getManagedCredential()))
  headers.delete("Content-Type")
  headers.set("Idempotency-Key", upload.requestId)
  const response = await fetchWithAuthRetry(sourcesUrl(agentRef, sessionId), {
    method: "POST",
    headers,
    body,
  })
  return ((await response.json()) as { source: HostedAgentSource }).source
}

export async function getHostedAgentSourceContentApi(
  agentRef: string,
  source: Pick<HostedAgentSource, "id" | "version">,
  options: { sessionId?: string | null; offset?: number } = {}
): Promise<HostedAgentSourceContent> {
  const response = await fetchWithAuthRetry(
    `${sourcesUrl(agentRef, options.sessionId)}/${encodeURIComponent(source.id)}/content?version=${source.version}&offset=${options.offset ?? 0}`,
    { headers: getHeaders(getManagedCredential()) }
  )
  return (await response.json()) as HostedAgentSourceContent
}

export async function downloadHostedAgentSourceApi(
  agentRef: string,
  source: Pick<HostedAgentSource, "id" | "version">,
  sessionId?: string | null
): Promise<Blob> {
  const response = await fetchWithAuthRetry(
    `${sourcesUrl(agentRef, sessionId)}/${encodeURIComponent(source.id)}/download?version=${source.version}`,
    { headers: getHeaders(getManagedCredential()) }
  )
  return response.blob()
}

export async function deleteHostedAgentSourceApi(
  agentRef: string,
  sessionId: string,
  source: Pick<HostedAgentSource, "id" | "version">
): Promise<void> {
  await fetchWithAuthRetry(
    `${sourcesUrl(agentRef, sessionId)}/${encodeURIComponent(source.id)}?version=${source.version}`,
    { method: "DELETE", headers: getHeaders(getManagedCredential()) }
  )
}
